import { segmentsStore } from '@/stores/segmentsStore'
import { mergeStore } from '@/stores/mergeStore'
import { pagingStore } from '@/stores/pagingStore'
import { documentStore } from '@/stores/documentStore';

import { SegmentEditorItem } from '@/models/SegmentEditorItem'
import { TranslationSegment } from '@/models/TranslationSegment'
import { SegmentEditorItemType } from '../SegmentEditorItemType'
import { DocumentType } from '@/models/DocumentType'
import { getMergeKey } from '@/models/segmentMergeKey'

import { segmentEditorItemFactory } from '../segmentEditorItemFactory'

import { DeltaMerger } from './deltaMerger'

import { segmentsApi } from '@/api/segmentsApi'

interface ParagraphContinuationResponse {
    segments: TranslationSegment[];
    hasMore: boolean;
}

interface IncreaseSegmentsResult {
    success: boolean;
    endOfParagraph: boolean;
}

export class mergeSegmentService {
    segmentsStore: any
    mergeStore: any
    pagingStore: any
    documentStore: any

    private readonly CONTINUATION_BATCH_SIZE = 10;

    constructor() {
        this.segmentsStore = segmentsStore()
        this.mergeStore = mergeStore()
        this.pagingStore = pagingStore()
        this.documentStore = documentStore();
    }

    private isDocxDocument(): boolean {
        return this.documentStore.documentType === DocumentType.Docx;
    }

    private shouldEnforceMergeBoundary(): boolean {
        return !this.isDocxDocument();
    }

    async initMerge(segmentId: number): Promise<boolean> {
        this.mergeStore.init();
        this.initMergeStore(segmentId);

        const isLast = this.segmentsStore.isLastSegmentOnPage(segmentId);
        const canMergeLocally = this.canMergeWithNextLocal(segmentId);

        if (isLast && !canMergeLocally) {
            const success = await this.loadInitialContinuation(segmentId);
            if (!success) {
                return false;
            }
        }

        this.markSegmentsToMerge();
        this.enableMerge();
        this.updateMergeStateFlags();
        return true;
    }

    private canMergeWithNextLocal(segmentId: number): boolean {
        const segmentIndex = this.segmentsStore.getSegmentPosition(segmentId);
        const currentSegment = this.segmentsStore.segmentsArray[segmentIndex];
        const nextSegment = this.segmentsStore.segmentsArray[segmentIndex + 1];

        if (!nextSegment) {
            return false;
        }

        if (nextSegment.segmentType !== SegmentEditorItemType.TranslationSegment) {
            return false;
        }

        if (this.shouldEnforceMergeBoundary()) {
            return getMergeKey(currentSegment.data) === getMergeKey(nextSegment.data);
        }

        return true;
    }

    private initMergeStore(segmentId: number) {
        this.mergeStore.setSegmentId(segmentId);
        const maxLocalCount = this.getMaxLocalMergeCount(segmentId);
        this.mergeStore.setMaxMergeSegmentsCount(maxLocalCount);
    }

    private getMaxLocalMergeCount(startSegmentId: number): number {
        const startIndex = this.segmentsStore.getSegmentPosition(startSegmentId);
        const startSegment = this.segmentsStore.segmentsArray[startIndex];
        const startMergeKey = getMergeKey(startSegment.data);

        let count = 1;
        for (let i = startIndex + 1; i < this.segmentsStore.segmentsArray.length; i++) {
            const segment = this.segmentsStore.segmentsArray[i];

            if (segment.segmentType !== SegmentEditorItemType.TranslationSegment) {
                break;
            }

            if (this.shouldEnforceMergeBoundary() && getMergeKey(segment.data) !== startMergeKey) {
                break;
            }

            count++;
        }

        return count;
    }

    private async loadInitialContinuation(segmentId: number): Promise<boolean> {
        this.mergeStore.setContinuationLoading(true);

        try {
            const segment = this.segmentsStore.getSegmentEditorItem(segmentId);
            const response = await this.fetchContinuationSegments(
                segmentId,
                segment.data.paragraphId
            );

            if (response.segments.length === 0) {
                this.mergeStore.setContinuationSegments([], false);
                return false;
            }

            const continuationItems = segmentEditorItemFactory
                .mapToTranslateSegmentEditorItems(response.segments);

            this.mergeStore.setContinuationSegments(continuationItems, response.hasMore);
            return true;
        } finally {
            this.mergeStore.setContinuationLoading(false);
        }
    }

    private async fetchContinuationSegments(
        afterSegmentId: number,
        paragraphId: string
    ): Promise<ParagraphContinuationResponse> {
        const enforceParagraph = this.shouldEnforceMergeBoundary();
        const response = await segmentsApi.getParagraphContinuation(
            afterSegmentId,
            this.CONTINUATION_BATCH_SIZE,
            enforceParagraph ? paragraphId : undefined
        );
        return response as ParagraphContinuationResponse;
    }

    async loadMoreContinuation(): Promise<boolean> {
        if (this.mergeStore.continuationChecked && !this.mergeStore.continuationHasMore) {
            return false;
        }

        this.mergeStore.setContinuationLoading(true);

        try {
            const lastContinuationSegment = this.getLastContinuationSegment();

            if (!lastContinuationSegment) {
                const lastLocalSegment = this.getLastLocalSegmentInMerge();
                if (!lastLocalSegment) {
                    this.mergeStore.setContinuationChecked(true);
                    return false;
                }

                const response = await this.fetchContinuationSegments(
                    lastLocalSegment.itemId,
                    lastLocalSegment.data.paragraphId
                );

                if (response.segments.length === 0) {
                    this.mergeStore.setContinuationSegments([], false);
                    return false;
                }

                const newItems = segmentEditorItemFactory
                    .mapToTranslateSegmentEditorItems(response.segments);

                this.mergeStore.setContinuationSegments(newItems, response.hasMore);
                return true;
            }

            const response = await this.fetchContinuationSegments(
                lastContinuationSegment.itemId,
                lastContinuationSegment.data.paragraphId
            );

            if (response.segments.length === 0) {
                this.mergeStore.setContinuationHasMore(false);
                this.mergeStore.setContinuationChecked(true);
                return false;
            }

            const newItems = segmentEditorItemFactory
                .mapToTranslateSegmentEditorItems(response.segments);

            this.mergeStore.appendContinuationSegments(newItems, response.hasMore);
            return true;
        } finally {
            this.mergeStore.setContinuationLoading(false);
        }
    }

    private getLastContinuationSegment(): SegmentEditorItem | null {
        const segments = this.mergeStore.continuationSegments;
        return segments.length > 0 ? segments[segments.length - 1] : null;
    }

    private getLastLocalSegmentInMerge(): SegmentEditorItem | null {
        const startPosition = this.segmentsStore.getSegmentPosition(this.mergeStore.segmentId);
        const localCount = Math.min(
            this.mergeStore.mergeSegmentsCount,
            this.mergeStore.maxMergeSegmentsCount
        );
        const lastLocalIndex = startPosition + localCount - 1;

        if (lastLocalIndex >= 0 && lastLocalIndex < this.segmentsStore.segmentsArray.length) {
            return this.segmentsStore.segmentsArray[lastLocalIndex];
        }
        return null;
    }

    getTotalAvailableSegments(): number {
        return this.mergeStore.maxMergeSegmentsCount +
            this.mergeStore.continuationSegments.length;
    }

    private needsMoreContinuation(): boolean {
        const wouldNeed = this.mergeStore.mergeSegmentsCount + 1;
        const available = this.getTotalAvailableSegments();

        return wouldNeed > available &&
            (!this.mergeStore.continuationChecked || this.mergeStore.continuationHasMore);
    }

    canStartMerge(segmentId: number): boolean {
        const segmentIndex = this.segmentsStore.getSegmentPosition(segmentId);
        const currentSegment = this.segmentsStore.segmentsArray[segmentIndex];
        const nextSegment = this.segmentsStore.segmentsArray[segmentIndex + 1];

        if (!nextSegment) {
            return false;
        }

        if (nextSegment.segmentType !== SegmentEditorItemType.TranslationSegment) {
            return false;
        }

        if (this.shouldEnforceMergeBoundary()) {
            if (getMergeKey(currentSegment.data) !== getMergeKey(nextSegment.data)) {
                return false;
            }
        }

        return true;
    }

    private markSegmentsToMerge() {
        const segmentsToMerge = this.getSegmentsToMerge();
        const segmentIdsToMerge = segmentsToMerge.map((s: SegmentEditorItem) => s.itemId);

        this.mergeStore.setMergeArray(segmentIdsToMerge);

        this.segmentsStore.segmentsArray.forEach((s: SegmentEditorItem) => {
            if (segmentIdsToMerge.includes(s.itemId) && s.segmentType == SegmentEditorItemType.TranslationSegment) {
                s.segmentIsVisibleOnList = false;
            }
            else {
                s.segmentIsVisibleOnList = true;
            }
        });
    }

    getSegmentsToMerge(): SegmentEditorItem[] {
        const startPosition = this.segmentsStore.getSegmentPosition(this.mergeStore.segmentId);
        const localAvailable = this.mergeStore.maxMergeSegmentsCount;
        const needed = this.mergeStore.mergeSegmentsCount;

        const localCount = Math.min(needed, localAvailable);
        const localSegments = this.segmentsStore.segmentsArray.slice(
            startPosition,
            startPosition + localCount
        );

        const neededFromContinuation = needed - localCount;
        if (neededFromContinuation > 0) {
            const continuationSlice = this.mergeStore.continuationSegments
                .slice(0, neededFromContinuation);
            return [...localSegments, ...continuationSlice];
        }

        return localSegments;
    }

    getMergeSourceData() {
        const segmentsToMerge = this.getSegmentsToMerge();
        const segmentsData = segmentsToMerge.map((s: SegmentEditorItem) => s.data);

        return DeltaMerger.buildMergedSourceData(segmentsData);
    }

    private enableMerge() {
        this.segmentsStore.setMergeState();
    }

    canMergeMore(): boolean {
        const currentCount = this.mergeStore.mergeSegmentsCount;
        const available = this.getTotalAvailableSegments();

        return currentCount < available ||
            !this.mergeStore.continuationChecked ||
            this.mergeStore.continuationHasMore;
    }

    private getNextAvailableSegment(): SegmentEditorItem | null {
        const segmentsToMerge = this.getSegmentsToMerge();
        if (segmentsToMerge.length === 0) {
            return null;
        }

        const lastSegmentInMerge = segmentsToMerge[segmentsToMerge.length - 1];
        const lastSegmentIndex = this.segmentsStore.getSegmentPosition(lastSegmentInMerge.itemId);

        // Check if next segment is in local segments
        if (lastSegmentIndex >= 0 && lastSegmentIndex + 1 < this.segmentsStore.segmentsArray.length) {
            const nextLocalSegment = this.segmentsStore.segmentsArray[lastSegmentIndex + 1];
            if (nextLocalSegment.segmentType === SegmentEditorItemType.TranslationSegment) {
                // Check if it's not already in the merge
                if (!this.mergeStore.mergeArray.includes(nextLocalSegment.itemId)) {
                    return nextLocalSegment;
                }
            }
        }

        // Check continuation segments
        const currentMergeCount = this.mergeStore.mergeSegmentsCount;
        const localCount = this.mergeStore.maxMergeSegmentsCount;
        const continuationIndex = currentMergeCount - localCount;

        if (continuationIndex >= 0 && continuationIndex < this.mergeStore.continuationSegments.length) {
            return this.mergeStore.continuationSegments[continuationIndex];
        }

        return null;
    }

    private updateMergeStateFlags(): void {
        const segmentsToMerge = this.getSegmentsToMerge();
        if (segmentsToMerge.length === 0) {
            this.mergeStore.setHasMoreSegments(false);
            this.mergeStore.setAtParagraphEnd(false);
            return;
        }

        const lastSegmentInMerge = segmentsToMerge[segmentsToMerge.length - 1];
        const nextSegment = this.getNextAvailableSegment();

        // Check if there are more segments at all
        const hasMore = this.canMergeMore();
        this.mergeStore.setHasMoreSegments(hasMore);

        if (!hasMore || !nextSegment) {
            this.mergeStore.setAtParagraphEnd(false);
            return;
        }

        // Check if next segment is in a different paragraph
        const crossesBoundary = getMergeKey(lastSegmentInMerge.data) !== getMergeKey(nextSegment.data);
        this.mergeStore.setAtParagraphEnd(crossesBoundary);
    }

    async increaseSegments(): Promise<IncreaseSegmentsResult> {
        if (this.needsMoreContinuation()) {
            const loaded = await this.loadMoreContinuation();

            const available = this.getTotalAvailableSegments();
            if (!loaded && this.mergeStore.mergeSegmentsCount >= available) {
                this.updateMergeStateFlags();
                return { success: false, endOfParagraph: true };
            }
        }

        if (this.mergeStore.mergeSegmentsCount < this.getTotalAvailableSegments()) {
            this.mergeStore.increaseMergeSegmentsCount();
            this.markSegmentsToMerge();
            this.updateMergeStateFlags();
            return { success: true, endOfParagraph: false };
        }

        this.updateMergeStateFlags();
        return { success: false, endOfParagraph: true };
    }

    decreaseSegments() {
        if (this.mergeStore.canDecrease) {
            this.mergeStore.decreaseMergeSegmentsCount();
            this.markSegmentsToMerge();
            this.updateMergeStateFlags();
        } else {
            this.cancelMerge();
        }
    }

    cancelMerge() {
        this.segmentsStore.segmentsArray.forEach((s: SegmentEditorItem) => {
            s.segmentIsVisibleOnList = true;
        })

        this.segmentsStore.setTranslateState();
        this.removeMergeSegment();
        this.mergeStore.clearContinuation();
    }

    private removeMergeSegment() {
        const segmentIndex = this.segmentsStore.segmentsArray.findIndex((s: SegmentEditorItem) => s.segmentType == SegmentEditorItemType.MergeSegment);
        if (segmentIndex >= 0) {
            this.segmentsStore.removeSegmentFromSegmentsArray(segmentIndex);
        }
    }

    async mergeSegments() {
        await this.getMergedSegment();
        this.segmentsStore.setTranslateState();
        this.mergeStore.clearContinuation();
        this.documentStore.reloadDocument();
    }

    private async getMergeResult() {
        //TODO: move to model
        const mergeRequest = {
            segmentIds: this.mergeStore.mergeArray,
            pageSize: this.pagingStore.pageSize,
        }

        return segmentsApi.mergeSegments(mergeRequest);
    }

    private async getMergedSegment() {
        const mergeResponce = await this.getMergeResult();
        const mergedSegment = mergeResponce as TranslationSegment;

        if (mergedSegment) {
            const rowCount = mergeResponce.rowCount;
            this.pagingStore.setRowCount(rowCount);
        }
    }
}